import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Avatar } from "../../components/UserBadge";
import { Search, Ban, Check, KeyRound, Edit3, X, Save } from "lucide-react";

const ROLES = ["user", "mod", "admin", "root"];

export default function IDGMembersTab() {
  const [members, setMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (statusFilter !== "all") params.set("status", statusFilter);
    api.get(`/idg/members?${params.toString()}`).then((r) => setMembers(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, statusFilter]);
  useEffect(() => { api.get("/idg/groups").then((r) => setGroups(r.data)); }, []);

  const updateMember = async () => {
    const payload = { ...editing };
    delete payload.id; delete payload._id; delete payload.username_lower;
    try {
      await api.patch(`/idg/members/${editing.id}`, payload);
      toast.success("Membru actualizat");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleBan = async (m) => {
    try {
      await api.patch(`/idg/members/${m.id}`, { banned: !m.banned });
      toast.success(m.banned ? "Deblocat" : "Blocat");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const suspend = async (id) => {
    const days = parseInt(window.prompt("Suspendă X zile:", "7"));
    if (!days || days < 1) return;
    try { await api.post(`/idg/members/${id}/suspend?days=${days}`); toast.success(`Suspendat ${days}z`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPassword = async (m) => {
    const np = window.prompt(`Resetează parola pentru ${m.username}:`);
    if (!np || np.length < 4) return;
    try { await api.patch(`/idg/members/${m.id}`, { new_password: np }); toast.success("Parolă resetată"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="idg-members-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input className="tf-input pl-9" placeholder="Caută email / username..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="idg-members-search" />
        </div>
        <select className="tf-input w-32" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="idg-members-filter">
          <option value="all">Toți</option>
          <option value="active">Activi</option>
          <option value="banned">Blocați</option>
        </select>
      </div>

      <div className="tf-surface tf-clip-card overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] tf-section-title border-b border-white/5 text-zinc-500">
          <div className="col-span-4">USER</div>
          <div className="col-span-3">EMAIL</div>
          <div className="col-span-1">ROL</div>
          <div className="col-span-1">PUNCTE</div>
          <div className="col-span-1">STATUS</div>
          <div className="col-span-2 text-right">ACȚIUNI</div>
        </div>
        <div className="divide-y divide-white/5">
          {members.map((m) => (
            <div key={m.id} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center" data-testid={`idg-member-row-${m.id}`}>
              <div className="col-span-4 flex items-center gap-2 min-w-0">
                <Avatar user={m} size={28} />
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{m.username}</div>
                  <div className="text-[10px] tf-mono text-zinc-500">{m.is_vip ? `VIP · ${m.vip_tier || ""}` : "Member"}</div>
                </div>
              </div>
              <div className="col-span-3 text-xs tf-mono text-zinc-400 truncate">{m.email}</div>
              <div className="col-span-1 text-xs">{m.role}</div>
              <div className="col-span-1 text-xs tf-mono">{m.points}</div>
              <div className="col-span-1">
                {m.banned ? (
                  <span className="text-[10px] px-1.5 py-0.5 bg-red-600 text-white tf-clip-card-sm uppercase font-bold">Blocat</span>
                ) : (
                  <span className="text-[10px] px-1.5 py-0.5 bg-green-600 text-white tf-clip-card-sm uppercase font-bold">Activ</span>
                )}
              </div>
              <div className="col-span-2 flex items-center gap-1 justify-end">
                <button onClick={() => setEditing(m)} className="text-zinc-400 hover:text-white p-1" title="Editează" data-testid={`idg-member-edit-${m.id}`}>
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => resetPassword(m)} className="text-zinc-400 hover:text-yellow-400 p-1" title="Reset parolă" data-testid={`idg-member-reset-${m.id}`}>
                  <KeyRound className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => suspend(m.id)} className="text-zinc-400 hover:text-orange-400 p-1" title="Suspendă" data-testid={`idg-member-suspend-${m.id}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => toggleBan(m)} className={`p-1 ${m.banned ? "text-green-500 hover:bg-green-500/10" : "text-red-500 hover:bg-red-500/10"}`} title={m.banned ? "Deblocează" : "Blochează"} data-testid={`idg-member-ban-${m.id}`}>
                  {m.banned ? <Check className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/80 z-[200] flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="tf-surface-elev tf-clip-card border border-white/10 max-w-2xl w-full p-5 space-y-3" data-testid="idg-member-edit-modal">
            <div className="flex items-center justify-between">
              <h3 className="tf-section-title">Editează: {editing.username}</h3>
              <button onClick={() => setEditing(null)}><X className="w-4 h-4 text-zinc-400 hover:text-white" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Username" value={editing.username} onChange={(v) => setEditing({ ...editing, username: v })} testid="member-username" />
              <Field label="Email" value={editing.email} onChange={(v) => setEditing({ ...editing, email: v })} testid="member-email" />
              <div>
                <label className="text-xs tf-section-title block mb-1">Rol</label>
                <select className="tf-input" value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} data-testid="member-role">
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <Field label="Puncte" type="number" value={editing.points} onChange={(v) => setEditing({ ...editing, points: parseInt(v) || 0 })} testid="member-points" />
              <Field label="Avatar URL" value={editing.avatar_url} onChange={(v) => setEditing({ ...editing, avatar_url: v })} testid="member-avatar" />
              <Field label="Banner URL" value={editing.banner_url} onChange={(v) => setEditing({ ...editing, banner_url: v })} testid="member-banner" />
            </div>
            <Field label="Bio" value={editing.bio} onChange={(v) => setEditing({ ...editing, bio: v })} textarea testid="member-bio" />
            
            {/* VIP Settings Panel */}
            <div className="p-3 bg-[#080b12] border border-amber-500/20 rounded space-y-3">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs font-bold text-amber-300 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={!!editing.is_vip} 
                    onChange={(e) => setEditing({ ...editing, is_vip: e.target.checked })} 
                    data-testid="member-is-vip" 
                  /> 
                  <span>Statut VIP Activ</span>
                </label>
                {editing.is_vip && (
                  <span className="text-[10px] font-mono text-amber-400">
                    {editing.vip_expires_at ? `Expiră: ${new Date(editing.vip_expires_at).toLocaleDateString()}` : "Permanent"}
                  </span>
                )}
              </div>

              {editing.is_vip && (
                <div className="space-y-2 pt-1 border-t border-amber-500/10">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] font-bold text-zinc-400 block mb-1 uppercase">Nivel VIP (Tier)</label>
                      <select 
                        className="tf-input text-xs" 
                        value={editing.vip_tier || "founder"} 
                        onChange={(e) => setEditing({ ...editing, vip_tier: e.target.value })}
                      >
                        <option value="founder">Founder VIP</option>
                        <option value="vip_diamond">Diamond VIP</option>
                        <option value="vip_gold">Gold VIP</option>
                        <option value="vip_silver">Silver VIP</option>
                        <option value="vip_bronze">Bronze VIP</option>
                        <option value="donator">Donator VIP</option>
                        <option value="monthly_winner">Monthly Winner</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-zinc-400 block mb-1 uppercase">Număr Achiziții</label>
                      <input 
                        type="number" 
                        min="1" 
                        className="tf-input text-xs" 
                        value={editing.vip_purchase_count || 1} 
                        onChange={(e) => setEditing({ ...editing, vip_purchase_count: parseInt(e.target.value) || 1 })} 
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-zinc-400 block mb-1 uppercase">Setează Durată / Perioadă VIP</label>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { label: "7 Zile", days: 7 },
                        { label: "30 Zile", days: 30 },
                        { label: "90 Zile", days: 90 },
                        { label: "180 Zile", days: 180 },
                        { label: "365 Zile", days: 365 },
                        { label: "Permanent", days: 0 },
                      ].map(preset => (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => {
                            if (preset.days === 0) {
                              setEditing({ ...editing, vip_expires_at: null, vip_duration_days: 0 });
                            } else {
                              const d = new Date();
                              d.setDate(d.getDate() + preset.days);
                              setEditing({ ...editing, vip_expires_at: d.toISOString(), vip_duration_days: preset.days });
                            }
                          }}
                          className="px-2 py-1 bg-[#101624] hover:bg-amber-500 hover:text-black border border-white/10 text-zinc-300 text-[10px] font-bold transition-colors"
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!editing.banned} onChange={(e) => setEditing({ ...editing, banned: e.target.checked })} data-testid="member-banned" /> Blocat</label>
            </div>
            <div>
              <label className="text-xs tf-section-title block mb-1">Grupuri</label>
              <div className="flex flex-wrap gap-2">
                {groups.map((g) => (
                  <label key={g.id} className="flex items-center gap-1.5 px-2 py-1 tf-surface tf-clip-card-sm border border-white/10 cursor-pointer text-xs">
                    <input type="checkbox" checked={(editing.group_ids || []).includes(g.id)} onChange={(e) => {
                      const gs = new Set(editing.group_ids || []);
                      if (e.target.checked) gs.add(g.id); else gs.delete(g.id);
                      setEditing({ ...editing, group_ids: Array.from(gs) });
                    }} data-testid={`member-group-${g.slug}`} />
                    <span style={{ color: g.color }}>{g.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-3 border-t border-white/5">
              <button onClick={updateMember} className="tf-btn-primary tf-clip-btn flex items-center gap-1" data-testid="idg-member-save"><Save className="w-3.5 h-3.5" /> Salvează</button>
              <button onClick={() => setEditing(null)} className="tf-btn-secondary tf-clip-btn">Anulează</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", textarea, testid }) {
  return (
    <div>
      <label className="text-xs tf-section-title block mb-1">{label}</label>
      {textarea ? (
        <textarea className="tf-input" rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      ) : (
        <input type={type} className="tf-input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      )}
    </div>
  );
}
